import React, { useState, useRef, useEffect } from 'react';
import ReportLayout from '../../components/Layout/ReportLayout';
import styles from '../../assets/styles/report/DataTable.module.scss';
import { motion, AnimatePresence } from 'framer-motion';
import { useReportFilter } from '../../contexts/ReportContext';
import { useProjectContext } from "../../contexts/ProjectContext";

const DataTable: React.FC = () => {
  const { selectedProjectId } = useProjectContext();
  const { selectedPeriod, customStart, customEnd } = useReportFilter();

  const [filterArea, setFilterArea] = useState('全国');
  const [filterGender, setFilterGender] = useState('すべて');
  const [filterAge, setFilterAge] = useState('すべて');

  const [tableData, setTableData] = useState<any[]>([]);
  // ★ 追加: 動的CVカラムのリスト
  const [cvColumns, setCvColumns] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedMedia, setSelectedMedia] = useState<string[]>([]);
  const [isMediaMenuOpen, setIsMediaMenuOpen] = useState(false);
  const mediaMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mediaMenuRef.current && !mediaMenuRef.current.contains(event.target as Node)) {
        setIsMediaMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      if (!selectedProjectId) return;
      try {
        const params: Record<string, string> = {
          project_id: String(selectedProjectId),
          period: selectedPeriod,
        };
        if (selectedPeriod === 'custom' && customStart && customEnd) {
          params.start = customStart;
          params.end = customEnd;
        }
        const queryParams = new URLSearchParams(params);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/get-report-datatable?${queryParams}`,
          { credentials: 'include' }
        );
        const json = await response.json();

        if (json.data && Array.isArray(json.data)) {
          setTableData(json.data);
          setCvColumns(json.cv_columns || []);
          setSelectedMedia(json.data.map((d: any) => d.name));
        } else {
          console.error("Unexpected response format:", json);
          setTableData([]);
          setCvColumns([]);
          setSelectedMedia([]);
        }
      } catch (error) {
        console.error("DataTable fetch error:", error);
        setTableData([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedProjectId, selectedPeriod, customStart, customEnd]);

  // 全媒体のリスト（チェックボックス用）
  const allMediaNames = tableData.map(d => d.name);

  // 選択されているものだけフィルタリング
  const filteredData = tableData.filter(d => selectedMedia.includes(d.name));

  const toggleMedia = (name: string) => {
    if (selectedMedia.includes(name)) {
      setSelectedMedia(selectedMedia.filter(m => m !== name));
    } else {
      setSelectedMedia([...selectedMedia, name]);
    }
  };

  const toggleAll = () => {
    if (selectedMedia.length === allMediaNames.length) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(allMediaNames);
    }
  };

  return (
    <ReportLayout>
      <div className={styles.flexColumn}>
        {/* Header / Filter Area */}
        <div className={styles.filterSection}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className={styles.mediaFilterWrapper} ref={mediaMenuRef}>
              <button
                onClick={() => setIsMediaMenuOpen(!isMediaMenuOpen)}
                className={styles.mediaFilterBtn}
              >
                表示媒体 ({selectedMedia.length}/{allMediaNames.length})
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
              </button>

              <AnimatePresence>
                {isMediaMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className={styles.mediaMenu}
                  >
                    <div className={styles.menuFooter}>
                      <label className={styles.mediaMenuItem}>
                        <input
                          type="checkbox"
                          checked={selectedMedia.length === allMediaNames.length && allMediaNames.length > 0}
                          onChange={toggleAll}
                          className={styles.menuCheckbox}
                        />
                        すべて選択
                      </label>
                    </div>
                    <div className={styles.flexColumn} style={{ gap: '4px' }}>
                      {allMediaNames.map(name => (
                        <label key={name} className={`${styles.mediaMenuItem} ${selectedMedia.includes(name) ? styles.active : ''}`}>
                          <input
                            type="checkbox"
                            checked={selectedMedia.includes(name)}
                            onChange={() => toggleMedia(name)}
                            className={styles.menuCheckbox}
                          />
                          {name}
                        </label>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className={styles.modalTitleArea}>
            <div className={styles.modalFilters}>
              {/* <div className={styles.filterItem}>
                <label>エリア</label>
                <select value={filterArea} onChange={(e) => setFilterArea(e.target.value)} className={styles.formSelect}>
                  <option>全国</option>
                  <option>関東</option>
                  <option>関西</option>
                  <option>中部</option>
                  <option>その他</option>
                </select>
              </div> */}
              {/* <div className={styles.filterItem}>
                <label>性別</label>
                <select value={filterGender} onChange={(e) => setFilterGender(e.target.value)} className={styles.formSelect}>
                  <option>すべて</option>
                  <option>男性</option>
                  <option>女性</option>
                </select>
              </div> */}
              {/* <div className={styles.filterItem}>
                <label>年齢</label>
                <select value={filterAge} onChange={(e) => setFilterAge(e.target.value)} className={styles.formSelect}>
                  <option>すべて</option>
                  <option>18-24</option>
                  <option>25-34</option>
                  <option>35-44</option>
                  <option>45-54</option>
                  <option>55+</option>
                </select>
              </div> */}
            </div>
          </div>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.tableStickyWrapper}>
            <table className={styles.overallTable}>
              <thead>
                <tr>
                  <th className={styles.stickyCol}>媒体</th>
                  <th>表示回数</th>
                  <th>クリック数</th>
                  <th>CTR</th>
                  <th>CPM</th>
                  <th>CPC</th>
                  <th>コスト</th>
                  <th>CV</th>
                  <th>CPA</th>
                  <th>CVR</th>

                  {cvColumns.map(cvName => (
                    <React.Fragment key={cvName}>
                      <th>{cvName}</th>
                      <th>{cvName} CPA</th>
                      <th>{cvName} 率</th>
                    </React.Fragment>
                  ))}

                  <th>セッション数</th>
                  <th>Eセッション</th>
                  <th>E率</th>
                  <th>E単価</th>
                  <th>総ユーザー</th>
                  <th>新規ユーザー</th>
                  <th>PV</th>
                  <th>平均E時間</th>
                  <th>EセッションPV</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={20 + cvColumns.length * 3} style={{ textAlign: 'center', padding: '20px' }}>読み込み中...</td></tr>
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan={20 + cvColumns.length * 3} style={{ textAlign: 'center', padding: '20px' }}>データがありません</td></tr>
                ) : (
                  filteredData.map((m, i) => (
                    <tr key={i} className={styles[m.type]}>
                      <td className={styles.stickyCol}>{m.name}</td>
                      <td>{m.impressions?.toLocaleString() ?? '-'}</td>
                      <td>{m.clicks?.toLocaleString() ?? '-'}</td>
                      <td>{m.ctr ?? '-'}</td>
                      <td>{m.cpm != null ? `¥${m.cpm.toLocaleString()}` : '-'}</td>
                      <td>{m.cpc != null ? `¥${m.cpc.toLocaleString()}` : '-'}</td>
                      <td>{m.cost != null ? `¥${m.cost.toLocaleString()}` : '-'}</td>
                      <td>{m.cv?.toLocaleString() ?? '-'}</td>
                      <td>{m.cpa != null ? `¥${m.cpa.toLocaleString()}` : '-'}</td>
                      <td>{m.cvr ?? '-'}</td>

                      {/* ★ 動的CVデータの表示 */}
                      {cvColumns.map(cvName => {
                        const d = m.dynamic_cv?.[cvName];
                        return (
                          <React.Fragment key={cvName}>
                            <td>{d?.count?.toLocaleString() ?? '-'}</td>
                            <td>{d?.cpa != null ? `¥${d.cpa.toLocaleString()}` : '-'}</td>
                            <td>{d?.rate ?? '-'}</td>
                          </React.Fragment>
                        );
                      })}

                      <td>{m.sessions?.toLocaleString() ?? '-'}</td>
                      <td>{m.engagedSessions?.toLocaleString() ?? '-'}</td>
                      <td>{m.engagementRate ?? '-'}</td>
                      <td>{m.engagementCost != null ? `¥${m.engagementCost.toLocaleString()}` : '-'}</td>
                      <td>{m.totalUsers?.toLocaleString() ?? '-'}</td>
                      <td>{m.newUsers?.toLocaleString() ?? '-'}</td>
                      <td>{m.pv?.toLocaleString() ?? '-'}</td>
                      <td>{m.avgEngagementTime ?? '-'}</td>
                      <td>{m.pvPerEngagedSession ?? '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ReportLayout>
  );
};

export default DataTable;